/**
 * Prix catalogue par devise.
 * Le prix euro du produit reste la référence (dashboard, TVA, bons).
 * currencyPrices[devise] est le montant TTC réellement payé, saisi sur la fiche.
 * Une case vide : on retombe sur le prix euro × taux.
 */

export const CATALOG_CURRENCIES = ['GBP', 'USD', 'CAD', 'CHF', 'JPY', 'KRW'];

/** Les trois tarifs de port réellement proposés au checkout. */
export const SHIPPING_FEE_TIERS = [0, 4.99, 9.99];

export const SHIPPING_CURRENCY_CONFIG_KEY = 'shipping-currency-prices';

export const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW'];

// Repli si aucun taux n'est configuré (currency-rate-*). Le yen et le won
// ne doivent pas retomber sur 1, sinon le port s'affiche à 4,99 ¥.
export const FALLBACK_RATES = {
	USD: 1,
	GBP: 1,
	CAD: 1.666889,
	CHF: 1,
	JPY: 179,
	KRW: 1610,
};

export function isZeroDecimalCurrency(currency) {
	return ZERO_DECIMAL_CURRENCIES.includes(`${currency || ''}`.toUpperCase());
}

export function roundForCurrency(amount, currency) {
	const value = parseFloat(amount) || 0;
	if(isZeroDecimalCurrency(currency)) {
		return Math.round(value);
	}
	return Math.round(value * 100) / 100;
}

/** Plus petite unité Stripe : le yen et le won n'ont pas de centimes. */
export function toStripeMinorUnits(amount, currency) {
	const value = parseFloat(amount) || 0;
	if(isZeroDecimalCurrency(currency)) {
		return Math.round(value);
	}
	return Math.round(value * 100);
}

export function fromStripeMinorUnits(amount, currency) {
	const value = parseFloat(amount) || 0;
	if(isZeroDecimalCurrency(currency)) {
		return Math.round(value);
	}
	return Math.round(value) / 100;
}

export function getCatalogCurrencyPrice(product, currency) {
	const code = `${currency || ''}`.toUpperCase();
	if(!code || code === 'EUR') {
		return null;
	}
	const raw = ((product || {}).currencyPrices || {})[code];
	if(raw === undefined || raw === null || `${raw}`.trim() === '') {
		return null;
	}
	const value = parseFloat(raw);
	return Number.isFinite(value) ? value : null;
}

/**
 * Montant d'une ligne dans la devise.
 * currencyPrices correspond à catalogBase (prix euro de la même base, en général le prix unitaire).
 * eurAmount est le montant euro réellement facturé sur la ligne (remises comprises).
 */
export function convertLineAmount({ eurAmount, currency, rate, currencyPrices, catalogBase, useCatalog = true }) {
	const code = `${currency || 'EUR'}`.toUpperCase();
	const eur = parseFloat(eurAmount) || 0;
	const catalog = useCatalog ? getCatalogCurrencyPrice({ currencyPrices }, code) : null;
	const base = parseFloat(catalogBase);
	if(code !== 'EUR' && catalog != null && base) {
		return roundForCurrency(catalog * (eur / base), code);
	}
	const appliedRate = code === 'EUR' ? 1 : (parseFloat(rate) || FALLBACK_RATES[code] || 1);
	return roundForCurrency(eur * appliedRate, code);
}

// Montant payé d'une ligne. Les livres en plus sont déjà chiffrés dans la devise
// (moitié du prix catalogue du tome), le premier exemplaire reste au prorata catalogue.
export function paidLineAmount(line, currency, rate) {
	const code = `${currency || 'EUR'}`.toUpperCase();
	const appliedRate = code === 'EUR' ? 1 : (parseFloat(rate) || FALLBACK_RATES[code] || 1);
	if(code === 'EUR') {
		return roundForCurrency(line?.eurAmount, 'EUR');
	}
	if(line?.extraCopies && line.extraCopies.count > 0) {
		const first = convertLineAmount({
			eurAmount: line.extraCopies.firstEur,
			currency: code,
			rate: appliedRate,
			currencyPrices: line.currencyPrices,
			catalogBase: line.catalogBase,
			useCatalog: line.useCatalog !== false,
		});
		return roundForCurrency(first + (line.extraCopies.count * line.extraCopies.unitInCurrency), code);
	}
	return convertLineAmount({
		eurAmount: line?.eurAmount,
		currency: code,
		rate: appliedRate,
		currencyPrices: line?.currencyPrices,
		catalogBase: line?.catalogBase,
		useCatalog: line?.useCatalog !== false,
	});
}

/** Clé "4.99" si le montant est un des trois tarifs, sinon null (supplément, autre prix). */
export function shippingFeeKey(amount) {
	const cents = Math.round((parseFloat(amount) || 0) * 100);
	const tier = SHIPPING_FEE_TIERS.find(fee => Math.round(fee * 100) === cents);
	return tier == null ? null : tier.toFixed(2);
}

export function parseShippingCurrencyTable(raw) {
	let data = raw;
	if(typeof raw === 'string' && raw.trim()) {
		try {
			data = JSON.parse(raw);
		} catch(err) {
			data = {};
		}
	}
	const source = data && typeof data === 'object' ? data : {};
	return Object.fromEntries(SHIPPING_FEE_TIERS.map(fee => {
		const key = fee.toFixed(2);
		return [key, sanitizeCurrencyPrices(source[key] || source[String(fee)] || {})];
	}));
}

export function currencyPricesForShippingFee(table, eurAmount) {
	const key = shippingFeeKey(eurAmount);
	if(!key || !table) {
		return null;
	}
	const prices = table[key];
	if(!prices || !Object.keys(prices).length) {
		return null;
	}
	return prices;
}

export function sumPaidTotals({ lines = [], shippingEur = 0, discountEur = 0, taxEur = 0, currency, rate, shippingCurrencyPrices = null }) {
	const code = `${currency || 'EUR'}`.toUpperCase();
	if(code === 'EUR') {
		return null;
	}
	const hasCatalog = lines.some(line => getCatalogCurrencyPrice(line, code) != null && line.useCatalog !== false);
	const hasShippingCatalog = !!shippingFeeKey(shippingEur) && getCatalogCurrencyPrice({ currencyPrices: shippingCurrencyPrices }, code) != null;
	if(!hasCatalog && !hasShippingCatalog) {
		return null;
	}
	const appliedRate = parseFloat(rate) || FALLBACK_RATES[code] || 1;
	const items = roundForCurrency(lines.reduce((sum, line) => sum + paidLineAmount(line, code, appliedRate), 0), code);
	const shippingFee = convertLineAmount({
		eurAmount: shippingEur,
		currency: code,
		rate: appliedRate,
		currencyPrices: shippingCurrencyPrices,
		catalogBase: shippingEur,
		useCatalog: !!shippingFeeKey(shippingEur),
	});
	const discount = roundForCurrency((parseFloat(discountEur) || 0) * appliedRate, code);
	const tax = roundForCurrency((parseFloat(taxEur) || 0) * appliedRate, code);
	return {
		items,
		shippingFee,
		discount,
		tax,
		totalTTC: roundForCurrency(items + shippingFee - discount, code),
	};
}

/** Props à passer à <Price> pour un produit ou une ligne de panier. */
export function getCatalogPriceProps(source) {
	if(!source || !source.currencyPrices) {
		return {};
	}
	const base = source.catalogBaseTTC != null && `${source.catalogBaseTTC}` !== ''
		? source.catalogBaseTTC
		: source.priceTTC;
	if(base == null || `${base}` === '') {
		return {};
	}
	return {
		catalogPrices: source.currencyPrices,
		catalogBase: base,
	};
}

/** Nettoie la saisie admin. Case vide = pas de prix catalogue. */
export function sanitizeCurrencyPrices(raw) {
	const out = {};
	if(!raw || typeof raw !== 'object') {
		return out;
	}
	CATALOG_CURRENCIES.forEach(code => {
		const value = raw[code];
		if(value === undefined || value === null || `${value}`.trim() === '') {
			return;
		}
		let text = `${value}`.trim().replace(/\s/g, '');
		if(isZeroDecimalCurrency(code)) {
			text = text.replace(/[.,]/g, '');
		} else {
			text = text.replace(',', '.');
		}
		const num = parseFloat(text);
		if(!Number.isFinite(num) || num < 0) {
			return;
		}
		out[code] = isZeroDecimalCurrency(code) ? String(Math.round(num)) : String(Math.round(num * 100) / 100);
	});
	return out;
}
